import React, { useMemo } from "react"
import { toggleDymamicContentTogglesTool } from "#/redux/actions/testUserActions"
import useShowBankDetails from "#/components/bank-details/hooks/useShowBankDetails"
import { useAuthentication } from "#/components/context/authentication/AuthenticationProvider"
import { useBenefitEnrollment } from "#/components/context/enrollment/BenefitEnrollmentContext"
import { useDispatch } from "react-redux"
import { UserDropdown as InternalUserDropdown } from "./user-dropdown"
import { useIntl } from "react-intl"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useFlags } from "launchdarkly-react-client-sdk"
import { useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"

function useGetUserDropdownName() {
  const currentUser = useCurrentUser()
  const firstName = currentUser?.firstName
  const preferredName = currentUser?.preferredName
  const lastName = currentUser?.lastName
  const intl = useIntl()

  const accountName = useMemo(() => {
    const defaultAccountName = intl.formatMessage({ defaultMessage: "Account" })
    if (preferredName) {
      return preferredName.length < 12 ? preferredName : defaultAccountName
    }
    return firstName?.length < 12 ? firstName : defaultAccountName
  }, [firstName, preferredName, intl])

  // Consumer members come back from the API without a last name (and sometimes
  // without a first name), so build the initials from whichever names actually
  // exist rather than indexing into them blindly.
  //
  // Only from real NAMES — never from `accountName`, which falls back to the
  // translated "Account" label. Initialling that would render an initial the member
  // does not have, in whatever language they happen to be reading ("C" for "Cuenta").
  // A member with no name at all therefore gets no initials, and the avatar is empty.
  const initials = useMemo(() => {
    const firstInitial = (preferredName || firstName || "")[0] ?? ""
    const lastInitial = lastName?.[0] ?? ""
    return `${firstInitial}${lastInitial}`
  }, [firstName, preferredName, lastName])

  return {
    name: accountName,
    initials: initials
  }
}

export function UserDropdown() {
  const intl = useIntl()
  const currentUser = useCurrentUser()
  const dispatch = useDispatch()
  const { name, initials } = useGetUserDropdownName()
  const { shouldDisplayInsuranceDetails } = useBenefitEnrollment()
  const { showBankDetails } = useShowBankDetails()
  const { handleSignOut } = useAuthentication()
  const { carrotConnectEnabled, consumerAppSignUp } = useFlags()
  const { data: programFlags } = useMemberProgramFlags()
  const isTestUser = currentUser?.isTestUser

  // Both gates can be undefined while the LD flags / member program query are in
  // flight, and the dropdown renders an item whose `condition` is undefined, so
  // coerce to a real boolean to keep the link hidden until both resolve true.
  const showSubscriptionAndBilling = Boolean(consumerAppSignUp && programFlags?.isConsumer)

  return (
    <InternalUserDropdown
      name={name}
      initials={initials}
      navigationLinks={[
        {
          condition: showSubscriptionAndBilling,
          id: "nav-subscription-billing-link",
          label: intl.formatMessage({ defaultMessage: "Subscription and billing" }),
          href: "/consumer/subscription-billing"
        },
        {
          id: "nav-account-settings-link",
          label: intl.formatMessage({ defaultMessage: "Account settings" }),
          href: "/account"
        },
        {
          condition: showBankDetails,
          id: "nav-bank-details-link",
          label: intl.formatMessage({ defaultMessage: "Bank details" }),
          href: "/bank-details"
        },
        {
          condition: shouldDisplayInsuranceDetails,
          id: "nav-insurance-details-link",
          label: intl.formatMessage({ defaultMessage: "Health insurance details" }),
          href: "/health-insurance-details"
        },
        {
          condition: carrotConnectEnabled,
          id: "nav-member-id-link",
          label: intl.formatMessage({ defaultMessage: "Member ID" }),
          href: "/member-id"
        },
        {
          condition: isTestUser,
          id: "nav-dynamic-content-tool",
          label: "Dynamic Content Tool", // intentionally not translated so it can be easily found by internal users
          onClick: (handleClose) => {
            handleClose()
            dispatch(toggleDymamicContentTogglesTool())
          }
        },
        {
          id: "nav-sign-out-link",
          label: intl.formatMessage({ defaultMessage: "Sign out" }),
          onClick: (handleClose) => {
            handleClose()
            handleSignOut()
          }
        }
      ]}
    />
  )
}
